"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function BroadcastForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary bg-card/40 backdrop-blur-md text-center rounded-lg">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">
          MESSAGE DELIVERED
        </p>
        <p className="font-default text-xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-default font-bold uppercase tracking-wider border-foreground text-foreground hover:bg-foreground hover:text-background rounded-lg">
          Send Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
                Full Name *
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-transparent border-0 border-b border-border rounded-md px-0 py-3 h-auto text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0 transition-all" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
                Email Address *
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@brand.com" {...field} className="bg-transparent border-0 border-b border-border rounded-md px-0 py-3 h-auto text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0 transition-all" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="brand_name" rules={{
      required: "Brand name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              Brand / Company Name *
            </FormLabel>
            <FormControl>
              <Input placeholder="Your Brand" {...field} className="bg-transparent border-0 border-b border-border rounded-md px-0 py-3 h-auto text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0 transition-all" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="service_interest" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              Service Required *
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="bg-transparent border-0 border-b border-border rounded-md px-0 py-3 h-auto text-foreground data-[placeholder]:text-muted-foreground focus-visible:border-primary focus-visible:ring-0 w-full">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border rounded-lg">
                  <SelectItem value="Influencer Campaigns">Influencer Campaigns</SelectItem>
                  <SelectItem value="Campaign Management">Campaign Management</SelectItem>
                  <SelectItem value="Brand Exposure">Brand Exposure</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10 rounded-lg">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-auto bg-primary text-primary-foreground font-default font-bold uppercase tracking-wider text-base py-5 rounded-lg hover:opacity-90 transition-all duration-150">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : <>
            Send Message
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function ContactSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2593" id="contact-us" className="relative bg-background text-foreground py-32 md:py-[200px] px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="hidden lg:block absolute left-8 top-1/2 -translate-y-1/2 pointer-events-none">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground [writing-mode:vertical-rl] rotate-180">
          PINE PLAYERZ
        </div>
      </div>

      <div ref={ref} id="contact-us" className={`max-w-7xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex flex-col items-center text-center mb-12 md:mb-16">
          <div className="h-px w-[120px] bg-primary mb-6" />
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary mb-8">
            GET IN TOUCH
          </p>
          <h2 className="font-default font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-foreground">
            READY TO SCALE{" "}
            <span className="text-primary">YOUR BRAND?</span>
          </h2>
          <p className="font-default text-lg md:text-xl text-muted-foreground max-w-[600px] mt-8 leading-relaxed">
            Contact us at <Link to="mailto:pine@pineplayerz.com" className="text-primary font-bold">pine@pineplayerz.com</Link> or use the form below to start your journey.
          </p>
        </div>

        <div className="max-w-[720px] mx-auto">
          <div className="relative bg-card border border-border p-8 sm:p-12 md:p-16 rounded-2xl shadow-xl">
            <FormProvider formKey="contact_form_home" formId="contact_form_home" sectionName="contact-us" defaultValues={{
            name: "",
            email: "",
            brand_name: "",
            service_interest: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            brand_name: {
              required: true
            },
            service_interest: {
              required: true
            }
          }} submitText="Send Message" successMessage="Message received. Our team will reach out within 24 hours." errorMessage="Transmission failed. Please try again.">
              <BroadcastForm />
            </FormProvider>
          </div>

          <div className="flex items-center justify-center gap-3 mt-8">
            <div className="h-px w-12 bg-border" />
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              PINE PLAYERZ MEDIA
            </p>
            <div className="h-px w-12 bg-border" />
          </div>
        </div>
      </div>
    </section>;
}